import React from 'react';
import { motion } from 'framer-motion';
import { 
  TrendingUp, 
  Home, 
  Wallet, 
  AlertCircle, 
  ArrowUpRight, 
  ArrowDownRight,
  Building2,
  Users,
  Briefcase
} from 'lucide-react';
import { Property, MaintenanceTask, AppNotification, Entity } from '../types';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, AreaChart, Area, XAxis, YAxis, CartesianGrid } from 'recharts';

// --- FIX TYPESCRIPT ---
// Definimos o MotionDiv como 'any' para evitar o erro "Property className does not exist"
const MotionDiv = motion.div as any;

interface DashboardProps {
  properties: Property[];
  tasks: MaintenanceTask[];
  notifications: AppNotification[];
  entities: Entity[];
  lang: 'en' | 'pt';
  selectedEntity: string;
  onSelectEntity: (id: string) => void;
}

const Dashboard: React.FC<DashboardProps> = ({ 
  properties, 
  tasks, 
  notifications,
  entities,
  lang,
  selectedEntity,
  onSelectEntity
}) => {
  
  // Filtering
  const filteredProperties = selectedEntity === 'all' 
    ? properties 
    : properties.filter(p => p.entityId === selectedEntity);

  // --- SAFE CALCULATIONS (Prevents crashes on bad data) ---
  const totalPortfolioValue = filteredProperties.reduce((sum, p) => sum + (p.currentValue || 0), 0);
  const totalRent = filteredProperties.reduce((sum, p) => sum + (p.monthlyRent || 0), 0);
  
  const occupiedCount = filteredProperties.filter(p => p.status === 'Occupied').length;
  const occupancyRate = filteredProperties.length > 0 
    ? (occupiedCount / filteredProperties.length) * 100 
    : 0;

  const totalExpenses = filteredProperties.reduce((sum, p) => sum + (p.expenses || 0), 0);
  const netIncome = totalRent - totalExpenses;

  // Chart Data Preparation
  const statusData = [
    { name: 'Occupied', value: occupiedCount, color: '#10b981' }, // Emerald-500
    { name: 'Vacant', value: filteredProperties.filter(p => p.status === 'Vacant').length, color: '#ef4444' }, // Red-500
    { name: 'Maintenance', value: filteredProperties.filter(p => p.status === 'Maintenance').length, color: '#f59e0b' }, // Amber-500
  ].filter(d => d.value > 0);

  // Helper for currency
  const formatCurrency = (val: number) => 
    new Intl.NumberFormat(lang === 'pt' ? 'pt-PT' : 'en-US', { 
      style: 'currency', 
      currency: 'EUR',
      maximumFractionDigits: 0 
    }).format(val || 0);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
  };

  // Recharts Custom Components
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-slate-900 border border-slate-700 p-2 rounded-lg shadow-xl">
          <p className="text-white text-xs font-bold">{payload[0].name}</p>
          <p className="text-emerald-400 text-xs">{payload[0].value} units</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto space-y-8 pb-24 overflow-y-auto h-full">
      {/* Entity Filter */}
      {entities.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
          <button 
            onClick={() => onSelectEntity('all')}
            className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${selectedEntity === 'all' ? 'bg-emerald-500 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'}`}
          >
            All Entities
          </button>
          {entities.map(e => (
            <button 
              key={e.id}
              onClick={() => onSelectEntity(e.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors flex items-center gap-1.5 ${selectedEntity === e.id ? 'bg-emerald-500 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'}`}
            >
              {e.type === 'Company' ? <Building2 className="w-3 h-3"/> : <Users className="w-3 h-3"/>}
              {e.name}
            </button>
          ))}
        </div>
      )}

      {/* KPI Grid */}
      <MotionDiv 
        variants={containerVariants}
        initial="hidden"
        animate="show"
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
      >
        <KpiCard 
          title={lang === 'pt' ? "Valor do Portfólio" : "Portfolio Value"}
          value={formatCurrency(totalPortfolioValue)}
          icon={Home}
          trend="+5.2%"
          trendUp={true}
          delay={0}
        />
        <KpiCard 
          title={lang === 'pt' ? "Renda Mensal" : "Monthly Rent"}
          value={formatCurrency(totalRent)}
          icon={Wallet}
          trend="+1.8%"
          trendUp={true}
          delay={0.1}
        />
        <KpiCard 
          title={lang === 'pt' ? "Taxa de Ocupação" : "Occupancy Rate"}
          value={`${occupancyRate.toFixed(1)}%`}
          icon={Users}
          trend={occupancyRate < 90 ? "-2.1%" : "+0.5%"}
          trendUp={occupancyRate >= 90}
          delay={0.2}
          alert={occupancyRate < 80}
        />
        <KpiCard 
          title={lang === 'pt' ? "Lucro Líquido (Est.)" : "Net Income (Est.)"}
          value={formatCurrency(netIncome)}
          icon={TrendingUp}
          trend="Stable"
          trendUp={true}
          delay={0.3}
        />
      </MotionDiv>

      {/* Main Content Split */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Left Col: Charts */}
        <MotionDiv variants={itemVariants} className="lg:col-span-2 space-y-6">
          
          {/* Portfolio Composition */}
          <div className="bg-slate-900/50 backdrop-blur-sm border border-slate-800 p-6 rounded-2xl">
            <h3 className="text-white font-bold mb-6 flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-emerald-500"/>
              {lang === 'pt' ? "Composição do Portfólio" : "Portfolio Composition"}
            </h3>
            <div className="h-64 w-full">
              {filteredProperties.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={filteredProperties.map((p, i) => ({ name: `P${i}`, value: p.currentValue || 0 }))}>
                    <defs>
                      <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
                    <XAxis dataKey="name" hide />
                    <YAxis hide />
                    <Tooltip contentStyle={{ backgroundColor: '#0f172a', borderColor: '#334155', color: '#fff' }} />
                    <Area type="monotone" dataKey="value" stroke="#10b981" fillOpacity={1} fill="url(#colorValue)" />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-slate-600 text-sm">No data available</div>
              )}
            </div>
          </div>

        </MotionDiv>

        {/* Right Col: Stats & Alerts */}
        <MotionDiv variants={itemVariants} className="space-y-6">
          
          {/* Occupancy Donut */}
          <div className="bg-slate-900/50 backdrop-blur-sm border border-slate-800 p-6 rounded-2xl">
            <h3 className="text-white font-bold mb-4">{lang === 'pt' ? "Estado de Ocupação" : "Occupancy Status"}</h3>
            <div className="h-48 relative">
               {statusData.length > 0 ? (
                 <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={statusData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {statusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} stroke="none" />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                    </PieChart>
                 </ResponsiveContainer>
               ) : (
                 <div className="absolute inset-0 flex items-center justify-center text-slate-600 text-xs">No properties</div>
               )}
               {/* Center Text */}
               <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <span className="text-2xl font-bold text-white">{filteredProperties.length}</span>
                  <span className="text-[10px] text-slate-500 uppercase tracking-wider">Total</span>
               </div>
            </div>
            
            <div className="mt-4 space-y-2">
              {statusData.map(d => (
                <div key={d.name} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2 text-slate-300">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: d.color }}></div>
                    {d.name}
                  </div>
                  <span className="font-mono text-slate-500">{d.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Critical Alerts */}
          {notifications.some(n => n.type === 'Critical') && (
            <div className="bg-red-500/10 border border-red-500/20 p-4 rounded-xl">
               <div className="flex items-center gap-2 text-red-400 font-bold text-sm mb-2">
                 <AlertCircle className="w-4 h-4" />
                 {lang === 'pt' ? "Atenção Necessária" : "Attention Needed"}
               </div>
               <div className="space-y-2">
                 {notifications.filter(n => n.type === 'Critical').slice(0, 3).map(n => (
                   <div key={n.id} className="text-xs text-red-200/80 bg-red-500/5 p-2 rounded border border-red-500/10">
                     {n.title}
                   </div>
                 ))}
               </div>
            </div>
          )}

        </MotionDiv>
      </div>
    </div>
  );
};

// KPI Card Component
const KpiCard = ({ title, value, icon: Icon, trend, trendUp, delay, alert }: any) => (
  <MotionDiv 
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay, duration: 0.4 }}
    className={`bg-slate-900/50 backdrop-blur-sm border p-5 rounded-2xl relative overflow-hidden group hover:border-emerald-500/30 transition-all ${alert ? 'border-red-500/30' : 'border-slate-800'}`}
  >
    <div className="flex justify-between items-start mb-4">
      <div className={`p-2.5 rounded-xl ${alert ? 'bg-red-500/20 text-red-400' : 'bg-slate-800 text-emerald-500'}`}>
        <Icon className="w-5 h-5" />
      </div>
      {trend && (
        <div className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${trendUp ? 'text-emerald-400 bg-emerald-500/10' : 'text-red-400 bg-red-500/10'}`}>
          {trendUp ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
          {trend}
        </div>
      )}
    </div>
    <div>
      <h3 className="text-slate-400 text-xs font-bold uppercase tracking-wider mb-1">{title}</h3>
      <p className="text-2xl font-bold text-white font-mono tracking-tight group-hover:text-emerald-400 transition-colors">{value}</p>
    </div>
  </MotionDiv>
);

export default Dashboard;